<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ $t('app.tabs.home') }}</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <ion-refresher slot="fixed" @ionRefresh="refreshData($event)">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>



      <template v-if="isLoading">
        <ion-card class="m-md md:max-w-[600px] md:mx-auto md:my-lg bg-white">
          <ion-card-header class="p-lg">
            <ion-skeleton-text class="w-[38%] h-[14px] mb-xs" :animated="true"></ion-skeleton-text>
            <ion-skeleton-text class="w-[80%] h-[24px] mt-0" :animated="true"></ion-skeleton-text>
          </ion-card-header>
          <ion-card-content class="px-md py-sm">
            <div class="text-left py-1">
              <ion-skeleton-text class="w-[40%] h-[30px]" :animated="true"></ion-skeleton-text>
            </div>
          </ion-card-content>
        </ion-card>

        <div class="px-sm py-md md:max-w-[600px] md:mx-auto md:px-lg">
          <ion-list-header class="px-md py-0 mb-1">
            <ion-skeleton-text class="w-[45%] h-[20px]" :animated="true"></ion-skeleton-text>
          </ion-list-header>
          <ion-list class="pt-1">
            <ion-item v-for="n in 2" :key="n" class="my-xs mx-0 rounded-sm bg-white"
                style="--padding-start: 16px; --padding-end: 16px; --padding-top: 8px; --padding-bottom: 8px;">
              <div class="mr-sm" slot="start">
                <ion-skeleton-text class="w-[32px] h-[32px] rounded-full" :animated="true"></ion-skeleton-text>
              </div>
              <ion-label>
                <ion-skeleton-text class="w-[65%] h-[20px] mb-lg mt-lg" :animated="true"></ion-skeleton-text>
                <ion-skeleton-text class="w-[85%] h-[14px] mb-md" :animated="true"></ion-skeleton-text>
                <ion-skeleton-text class="w-[35%] h-[12px]" :animated="true"></ion-skeleton-text>
              </ion-label>
            </ion-item>
          </ion-list>
        </div>
      </template>

      <template v-else>
        <ion-card class="m-md md:max-w-[600px] md:mx-auto md:my-lg bg-white animate-scale-in" data-cy="welcome-card">
          <ion-card-header class="p-lg">
            <ion-card-subtitle class="text-sm mb-xs">{{ $t('common.welcome') }}</ion-card-subtitle>
            <ion-card-title class="text-2xl font-bold -tracking-tight" data-cy="user-name">{{ userStore.user?.display_name }}</ion-card-title>
          </ion-card-header>
          <ion-card-content>
            <div class="text-left max-w-[100px] pt-sm">
              <ion-text color="medium" class="text-sm">{{ $t('common.totalPoints') }}</ion-text>
              <div class="text-2xl font-bold text-primary points-display" data-cy="points-display">
                {{ userStore.userPoints }}
              </div>
            </div>

            <!-- Points gained this month - whitelabel configurable -->
            <div 
              v-if="whitelabelConfig.config.homePageConfig?.showMonthlyProgress !== false"
              class="flex items-center mt-1 text-md"
            >
              <ion-icon :icon="trendingUpOutline" color="success" class="mr-1"></ion-icon>
              <span class="text-green-500 font-bold">+{{ monthlyPointsGained }}</span>
              <span class="text-text-secondary ml-1">{{ $t('common.thisMonth') }}</span>
            </div>
          </ion-card-content>
        </ion-card>

        <div class="px-sm py-md md:max-w-[600px] md:mx-auto md:px-lg" data-cy="activity-section">
          <ion-list-header>{{ $t('common.recentActivity') }}</ion-list-header>
          <ion-list>
            <ion-item
              v-for="activity in localizedActivities"
              :key="activity.id"
              class="my-xs mx-0 rounded-sm bg-white"
              style="--padding-start: 16px; --padding-end: 16px; --padding-top: 8px; --padding-bottom: 8px;"
              data-cy="activity-item"
            >
              <ion-icon
                :icon="activity.icon"
                :color="activity.color"
                slot="start"
                size="large"
                class="mr-sm"
              ></ion-icon>
              <ion-label>
                <h2 class="text-base font-semibold text-text-primary mb-xs" data-cy="activity-title">{{ activity.title }}</h2>
                <p class="text-sm text-text-secondary mb-xs" data-cy="activity-description">{{ activity.description }}</p>
                <p class="text-xs text-text-tertiary" data-cy="activity-date">{{ formatDate(activity.date) }}</p>
              </ion-label>
            </ion-item>
          </ion-list>
        </div>
      </template>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useUserStore } from '../stores/userStore'
import { useI18n } from 'vue-i18n'
import { useWhitelabelConfig } from '../composables/useWhitelabelConfig'

import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonCard, IonCardHeader, IonCardTitle, IonCardSubtitle,
  IonCardContent, IonList, IonListHeader, IonItem, IonLabel,
  IonIcon, IonText, IonRefresher, IonRefresherContent, IonSkeletonText
} from '@ionic/vue'
import {
  ribbonOutline, trophyOutline, trendingUpOutline
} from 'ionicons/icons'

const userStore = useUserStore()
const i18n = useI18n()
const whitelabelConfig = useWhitelabelConfig()
const isLoading = ref(true)

// Whitelabel-configurable monthly points display - Subtask 7.3
const monthlyPointsGained = computed(() => {
  return whitelabelConfig.config.homePageConfig?.monthlyPointsGained ?? 1350
})

// Define activities with original (untranslated) values
const recentActivity = ref([
  {
    id: 1,
    titleKey: 'home.pointsEarned',
    descriptionKey: 'home.pointsFromPurchase',
    descriptionParams: { points: 50 },
    date: new Date(Date.now() - 24 * 60 * 60 * 1000),
    icon: ribbonOutline,
    color: 'success'
  },
  {
    id: 2,
    titleKey: 'home.achievementUnlocked',
    descriptionKey: 'home.reachedTop3',
    date: new Date(Date.now() - 48 * 60 * 60 * 1000),
    icon: trophyOutline,
    color: 'warning'
  }
])

// Computed property to localize activities
const localizedActivities = computed(() => {
  return recentActivity.value.map(activity => ({
    ...activity,
    title: i18n.t(activity.titleKey),
    description: activity.descriptionParams 
      ? i18n.t(activity.descriptionKey, activity.descriptionParams) 
      : i18n.t(activity.descriptionKey)
  }))
})

onMounted(async () => {
  await loadData()
})

async function loadData() {
  isLoading.value = true
  try {
    // Skip delay in test environment
    if (!import.meta.env.CYPRESS) {
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000))
    }
    await userStore.loadUser()
  } finally {
    isLoading.value = false
  }
}

async function refreshData(event: CustomEvent) {
  await loadData()
  
  if (event && event.target) {
    // Type assertion to handle the complete method
    const refresher = event.target as HTMLIonRefresherElement
    if (typeof refresher.complete === 'function') {
      refresher.complete()
    }
  }
}

function formatDate(date: Date) {
  const daysDiff = Math.round((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000))
  
  if (daysDiff === 0) {
    return i18n.t('dateFormat.today')
  } else if (daysDiff === 1) {
    return i18n.t('dateFormat.yesterday')
  } else {
    return i18n.t(daysDiff === 1 ? 'dateFormat.dayAgo' : 'dateFormat.daysAgo', { n: daysDiff })
  }
}

// Note: Global window assignment removed for better architecture compliance
// Tests should mock the refreshData function directly instead of relying on global window
</script> 